import { useCallback, useEffect, useRef } from 'react';

import { isInExoSkeleton, report, REPORT_TYPES } from '@adsk/exo-skeleton-api';
import { wrapWithEventPropertiesPrefix } from '@adsk/exo-skeleton-api/internal';

export const reportLaunchDarklyError = (
  message: string,
  context?: Record<string, unknown>,
) => {
  try {
    console.error(message);
    if (!isInExoSkeleton()) return;

    report({
      scope: 'ACCExoSkeleton',
      type: REPORT_TYPES.error,
      value: message,
      context: {
        ...context,
        ...wrapWithEventPropertiesPrefix({
          ...context,
          message,
          type: REPORT_TYPES.error,
        }),
      },
    });
  } catch {
    console.error('LaunchDarkly failed to report error:', message);
  }
};

export const getErrorSuffix = (error: unknown) => {
  try {
    return error instanceof Error && error.message ? `: ${error.message}` : '';
  } catch {
    return '';
  }
};

export const reportInitializationError = (error: unknown) => {
  reportLaunchDarklyError(
    `LaunchDarkly failed to initialize SDK${getErrorSuffix(error)}`,
  );
};

export const reportIdentifyUserError = (error: unknown) => {
  reportLaunchDarklyError(
    `LaunchDarkly failed to identify user${getErrorSuffix(error)}`,
  );
};

export const useWarnMissingDefaultOnce = <FlagValue>(
  flagName: string,
  defaultValue?: FlagValue,
) => {
  // `[]` deps run the effect once on mount, so we evaluate the initial
  // defaultValue exactly once per hook instance without re-running on every
  // render. The warning is intentionally based on the mount-time argument.
  useEffect(() => {
    if (defaultValue === undefined || defaultValue === null) {
      console.warn(
        `[LaunchDarkly] Feature flag '${flagName}' was used without a safe default value. ` +
          'Provide an explicit default to ensure safe fallback behavior.',
      );
    }
    // Intentionally only run on mount — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export const useReportErrorOnce = () => {
  const reported = useRef(false);
  return useCallback((message: string, context?: Record<string, unknown>) => {
    if (reported.current) return;
    reported.current = true;
    reportLaunchDarklyError(message, context);
  }, []);
};
